import React, { useState } from "react";
import { ReportAPI } from "../../api/apiClient";
import { useUI } from "../../context/UIContext";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

interface Row {
  componentGroup: string;
  component: string;
  date: string;
  reportType: string;
  mobilization: string;
  quantity: number;
  unit: string;
  vendor: string;
}

export default function EquipmentStatementVendorWiseReport() {

  const { selectedProjectId, selectedProjectName } = useUI();

  const [fromDate,setFromDate] = useState("");
  const [toDate,setToDate] = useState("");

  const [rows,setRows] = useState<Row[]>([]);
  const [loading,setLoading] = useState(false);

  const [expanded,setExpanded] = useState<string | null>(null);

  const [view,setView] = useState<"filter" | "report">("filter");


/* =============================
   DATE FORMAT
============================= */

const formatDate = (dateStr:string) => {

  const date = new Date(dateStr);

  const day = date.getDate().toString().padStart(2,"0");

  const month = date.toLocaleString("en-GB",{month:"short"});

  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};


/* =============================
   LOAD REPORT
============================= */

const loadReport = async () => {

  if (!selectedProjectId) return;

  setLoading(true);

  try {

    const data = await ReportAPI.getEquipmentStatementVendorWise({
      project: selectedProjectId,
      qDate: fromDate,
      endDate: toDate,
      vendor: -1,
      cmp: 0
    });

    setRows(data);

    setView("report");

  } catch(err:any) {

    alert(err.message);

  }

  setLoading(false);
};


/* =============================
   EXPAND ROW
============================= */

const toggle = (component:string) => {

  if(expanded === component)
    setExpanded(null);
  else
    setExpanded(component);

};


const grouped = Object.values(
  rows.reduce((acc:any,row)=>{

    if(!acc[row.component])
      acc[row.component] = {component:row.component,items:[]}

    acc[row.component].items.push(row)

    return acc

  },{})
);


/* =============================
   PDF EXPORT
============================= */

const exportPDF = () => {

  const pdf = new jsPDF("p","mm","a4");

  const pageWidth = pdf.internal.pageSize.getWidth();

  pdf.setFontSize(14);

  pdf.text(
    `${selectedProjectName}: Equipment Statement Vendor-wise`,
    14,
    15
  );

  pdf.setFontSize(10);

  pdf.text(
    `FROM ${formatDate(fromDate)} TO ${formatDate(toDate)}`,
    14,
    22
  );

  const tableRows:any[] = [];

  grouped.forEach((g:any)=>{

    tableRows.push([
      g.component,
      "Transient",
      g.items[0].unit,
      ""
    ]);

    if(expanded === g.component){

      g.items.forEach((r:Row)=>{

        tableRows.push([
          "   " + formatDate(r.date),
          "Transient",
          r.unit,
          ""
        ]);

      });

    }

  });

  autoTable(pdf,{
    startY:28,
    head:[["Resource","Type","Unit","Mobilization"]],
    body:tableRows,
    theme:"grid",
    styles:{ fontSize:10 },
    headStyles:{ fillColor:[31,41,55] }
  });

  const pageCount = pdf.getNumberOfPages();

  for(let i=1;i<=pageCount;i++){

    pdf.setPage(i);

    pdf.setFontSize(9);

    pdf.text(
      `Page ${i} of ${pageCount}`,
      pageWidth-30,
      290
    );

  }

  pdf.save("EquipmentStatementVendorWise.pdf");

};


/* =============================
   SCREEN 1 : FILTER SCREEN
============================= */

if(view === "filter"){

return(

<div className="p-6 space-y-6">

<h2 className="text-xl font-semibold">
Equipment Statement Vendor-wise Report
</h2>

<div className="space-y-4 max-w-md">

<div>
<label className="block text-sm font-medium mb-1">
From Date
</label>

<input
type="date"
value={fromDate}
onChange={(e)=>setFromDate(e.target.value)}
className="border p-2 rounded w-full"
/>
</div>


<div>
<label className="block text-sm font-medium mb-1">
To Date
</label>

<input
type="date"
value={toDate}
onChange={(e)=>setToDate(e.target.value)}
className="border p-2 rounded w-full"
/>
</div>


<div className="flex gap-3">

<button
onClick={loadReport}
className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
>
Run Report
</button>

</div>

</div>

</div>

);

}


/* =============================
   SCREEN 2 : REPORT SCREEN
============================= */

return(

<div className="p-6 space-y-4">

<div className="flex justify-end">

<button
onClick={exportPDF}
disabled={rows.length === 0}
className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:bg-gray-400"
>
Export PDF
</button>

</div>


<div className="text-lg font-semibold text-gray-800">
{selectedProjectName}: Equipment Statement Vendor-wise
</div>


<div className="text-sm font-bold text-gray-700 mb-4">
FROM {formatDate(fromDate)} TO {formatDate(toDate)}
</div>


{/* Table Header */}

<div className="grid grid-cols-4 bg-gray-800 text-white text-base px-4 py-2">

<div>Resource</div>
<div>Type</div>
<div>Unit</div>
<div>Mobilization</div>

</div>


{/* Vendor (only when data exists) */}

{rows.length > 0 && (
<div className="bg-red-200 px-4 py-2 font-semibold">
{rows[0]?.vendor || "N/A"}
</div>
)}


{/* No Data UI */}

{rows.length === 0 && !loading && (

<div className="flex justify-center items-center py-10 border border-gray-200">

<div className="text-lg font-semibold text-gray-700">
No data for the selected date range
</div>

</div>

)}


{/* Data Rows */}

{rows.length > 0 && (

<>

{grouped.map((g:any,i)=>(

<div key={i}>

<div
className="grid grid-cols-4 px-4 py-2 text-base cursor-pointer hover:bg-gray-50"
onClick={()=>toggle(g.component)}
>

<div className="flex items-center gap-2">

<span className="font-bold text-gray-700">
{expanded === g.component ? "−" : "+"}
</span>

{g.component}

</div>

<div>Transient</div>
<div>{g.items[0].unit}</div>
<div></div>

</div>


{expanded === g.component && (

g.items.map((r:Row,index:number)=>(

<div
key={index}
className="grid grid-cols-4 px-4 py-2 text-base text-black border-t bg-gray-100"
>

<div className="pl-6 font-semibold text-gray-900">
{formatDate(r.date)}
</div>

<div>Transient</div>

<div>{r.unit}</div>

<div></div>

</div>

))

)}

</div>

))}

<div className="h-6 bg-gray-800 mt-3"></div>

</>

)}


<div className="mt-4">

<button
onClick={()=>setView("filter")}
className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
>
Back
</button>

</div>

</div>

);

}